import React from 'react'
import { Button, ButtonGroup, Col, Container, Dropdown, FormLabel, Row } from 'react-bootstrap'
import { Listing } from '../../components/ui/Listing/Listing'
import { dollarFormat } from '../../utilities/format'
import { DateTime } from 'luxon'
import { AppContext } from '../../App'
import * as Request from '../../utilities/request'
import { CostWithJobInformation, ImportCost } from '../../../../back-end/common/costs'
import UploadModal from '../../components/costs/uploadModal'
import LoadingModal from '../../components/costs/loadingModal'
import * as I from '../../utilities/me'
import { ClientGroup } from '../../../../back-end/common/clientGroup'
import { Client } from '../../../../back-end/common/client'
import { Staff } from '../../../../back-end/common/staff'
import { useStateParams } from '../../utilities/hooks'
import { FormSelect, SelectOption } from '../../components/ui/Form/Select'
import { Director } from '../../constants/permission'
import { ChevronDown, ChevronRight } from 'react-feather'
import { FormDate } from '../../components/ui/Form/Date'

const Costs = () => {
	const { appState } = React.useContext(AppContext)
	const [costs, setCosts] = React.useState<CostWithJobInformation[] | null>(null)
	const [showModal, setShowModal] = React.useState<boolean>(false)
	const [showSearchFilters, setShowSearchFilters] = React.useState<boolean>(false)
	const [filterHasChangedWithoutSearch, setFilterHasChangedWithoutSearch] = React.useState<boolean>(false)

	// Filter Data
	const [groups, setGroups] = React.useState<ClientGroup[] | null>(null)
	const [clients, setClients] = React.useState<Client[] | null>(null)
	const [staff, setStaff] = React.useState<Staff[] | null>(null) // For Directors(partners)

	const [groupFilter, setGroupFilter] = useStateParams(
		'',
		'group',
		(d) => d,
		(d) => d
	)
	const [clientFilter, setClientFilter] = useStateParams(
		'',
		'client',
		(d) => d,
		(d) => d
	)
	const [directorFilter, setDirectorFilter] = useStateParams(
		'',
		'director',
		(d) => d,
		(d) => d
	)
	const [costStartDateFilter, setCostStartDateFilter] = useStateParams<DateTime | null>(
		null,
		'costStartDate',
		(d) => (d ? d.toJSON() || '' : ''),
		(d) => (d ? DateTime.fromISO(d) : null)
	)
	const [costEndDateFilter, setCostEndDateFilter] = useStateParams<DateTime | null>(
		null,
		'costEndDate',
		(d) => (d ? d.toJSON() || '' : ''),
		(d) => (d ? DateTime.fromISO(d) : null)
	)
	const [uploadStartDateFilter, setUploadStartDateFilter] = useStateParams<DateTime | null>(
		DateTime.now().minus({ months: 2 }).startOf('month'),
		'uploadStartDate',
		(d) => (d ? d.toJSON() || '' : ''),
		(d) => (d ? DateTime.fromISO(d) : null)
	)
	const [uploadEndDateFilter, setUploadEndDateFilter] = useStateParams<DateTime | null>(
		null,
		'uploadEndDate',
		(d) => (d ? d.toJSON() || '' : ''),
		(d) => (d ? DateTime.fromISO(d) : null)
	)

	React.useEffect(() => {
		setFilterHasChangedWithoutSearch(true)
	}, [groupFilter, clientFilter, directorFilter, costStartDateFilter, costEndDateFilter, uploadStartDateFilter, uploadEndDateFilter])

	const buildWhereString = React.useCallback(() => {
		const whereStringArray = []
		if (groupFilter) {
			whereStringArray.push(`where=ClientGroupID==${groupFilter}`)
		}
		if (clientFilter) {
			whereStringArray.push(`where=ClientID==${clientFilter}`)
		}
		if (directorFilter) {
			whereStringArray.push(`where=ClientAccountManagerStaffXpmID==${directorFilter}`)
		}
		if (costStartDateFilter) {
			whereStringArray.push(`where=CostDate>=${costStartDateFilter}`)
		}
		if (costEndDateFilter) {
			whereStringArray.push(`where=CostDate<=${costEndDateFilter}`)
		}
		if (uploadStartDateFilter) {
			whereStringArray.push(`where=CreateTs>=${uploadStartDateFilter}`)
		}
		if (uploadEndDateFilter) {
			whereStringArray.push(`where=CreateTs<=${uploadEndDateFilter}`)
		}
		return whereStringArray.join('&')
	}, [groupFilter, clientFilter, directorFilter, costStartDateFilter, costEndDateFilter, uploadStartDateFilter, uploadEndDateFilter])

	const [showLoadingModal, setShowLoadingModal] = React.useState<boolean>(false)
	const [loadingModalPercentage, setLoadingModalPercentage] = React.useState<number>(0)
	const [loadingModalMessage, setLoadingModalMessage] = React.useState<string>('')

	React.useEffect(() => {
		const getData = async () => {
			const where = buildWhereString()
			const [costsRes, clientGroupsRes, staffRes] = await Promise.all([
				Request.get(`cost?minify${where !== '' ? `&${where}` : ''}`),
				Request.get('clientgroup'),
				Request.get('staff'),
				Request.getPagedData(`client`, 4000, (data) => setClients((clients) => [...(clients || []), ...(data as { clients: Client[] }).clients])),
			])

			setCosts(costsRes.data.costs)
			setGroups(clientGroupsRes.data.clientGroups)
			setStaff(staffRes.data.staff)
		}

		if (appState.authState.isLoggedIn) {
			getData()
		}
	}, [appState.authState])

	const handleNewFilterSearch = () => {
		const getData = async () => {
			const where = buildWhereString()
			const [costsRes] = await Promise.all([Request.get(`cost?minify${where !== '' ? `&${where}` : ''}`)])

			setCosts(costsRes.data.costs)
			setFilterHasChangedWithoutSearch(false)
		}

		if (appState.authState.isLoggedIn) {
			getData()
		}
	}

	const getFilterDescription = () => {
		return (
			<span style={{ lineHeight: '42px' }}>
				{'Showing '}
				<span className="span-bold span-blue">{costs ? costs.length : '...'}</span>
				{' Costs'}
				{groupFilter !== '' && (
					<>
						{', for '}
						<span className="span-bold span-blue">{groups?.find((e) => e.clientGroupID === groupFilter)?.name}</span>
						{' group'}
					</>
				)}
				{clientFilter !== '' && (
					<>
						{', for client '}
						<span className="span-bold span-blue">{clients?.find((e) => e.clientID === clientFilter)?.name}</span>
					</>
				)}
				{directorFilter !== '' && (
					<>
						{', for partner '}
						<span className="span-bold span-blue">{directorOptions?.find((e) => e.value === directorFilter)?.label}</span>
					</>
				)}
				{costStartDateFilter && (
					<>
						{', from '}
						<span className="span-bold span-blue">{costStartDateFilter.toFormat('d MMMM yyyy')}</span>
					</>
				)}
				{costEndDateFilter && (
					<>
						{costStartDateFilter ? ' to ' : ', before '}
						<span className="span-bold span-blue">{costEndDateFilter.toFormat('d MMMM yyyy')}</span>
					</>
				)}
				{uploadStartDateFilter && (
					<>
						{', uploaded from '}
						<span className="span-bold span-blue">{uploadStartDateFilter.toFormat('d MMMM yyyy')}</span>
					</>
				)}
				{uploadEndDateFilter && (
					<>
						{uploadStartDateFilter ? ' to ' : ', uploaded before '}
						<span className="span-bold span-blue">{uploadEndDateFilter.toFormat('d MMMM yyyy')}</span>
					</>
				)}
			</span>
		)
	}

	const handleCostInsert = React.useCallback(
		async (costs: ImportCost[]) => {
			// check if we need to get any jobs
			setShowModal(false)
			setShowLoadingModal(true)
			setLoadingModalMessage("This can take some time, please be patient and don't close this window or navigate away.")
			setLoadingModalPercentage(1)

			const jobRes = await Request.post('cost/jobs', costs)
			setLoadingModalPercentage(10)
			if (!jobRes.data.success) {
				const regexResult = /To fetch: (\d+)/g.exec(jobRes.data.message)
				const numJobsToFetch = Number(regexResult?.[1]) || 0
				console.log('message', jobRes.data.message, 'regexResult', regexResult, 'numJobsToFetch', numJobsToFetch)
				for (let i = 0; i < Math.ceil(numJobsToFetch / 4); i++) {
					console.log('i', i)
					await Request.sleep(5000)
					setLoadingModalPercentage(10 + (i / Math.ceil(numJobsToFetch / 4)) * 80)
				}
			}

			const importRes = await Request.post('cost/import', costs)
			setLoadingModalPercentage(100)
			if (!importRes.data.success) {
				setLoadingModalMessage(importRes.data.message)
			} else {
				setLoadingModalMessage(`Successfully loaded ${importRes.data.costs.length} costs`)
				setCosts((c) => [...(c || []), ...importRes.data.costs])
			}
		},
		[setCosts, setShowModal, appState.authState]
	)

	const handleDownloadTemplate = () => {
		const file = new Blob(['jobNumber,description,price,date,code,cost,supplier,note,billable,quantity'], { type: 'text/csv' })
		const link = document.createElement('a')
		link.href = window.URL.createObjectURL(file)
		link.download = 'billy-costs-template.csv'
		link.click()
	}

	const handleLoadingClose = () => {
		setShowLoadingModal(false)
		setLoadingModalMessage('')
		setLoadingModalPercentage(0)
	}

	const groupOptions = React.useMemo(() => groups?.map((g) => ({ value: g.clientGroupID, label: g.name })), [groups])
	const clientOptions = React.useMemo(() => clients?.map((c) => ({ value: c.clientID, label: c.name })), [clients])
	const directorOptions = React.useMemo(() => staff?.filter((d) => d.permissionID === Director).map((d) => ({ value: d.xpmID, label: d.name })), [staff])

	return (
		<Container fluid>
			<Row className="background-light align-items-center py-3">
				<Col>{getFilterDescription()}</Col>
				<Col sm="auto">
					{!showSearchFilters && <div style={{ display: 'inline', paddingRight: '15px', color: 'red' }}></div>}
					<Button onClick={() => setShowSearchFilters(!showSearchFilters)} variant="secondary" className="small-filter-button">
						Filters {showSearchFilters ? <ChevronDown /> : <ChevronRight />}
					</Button>
				</Col>
			</Row>
			{showSearchFilters && (
				<>
					<Row className="py-3">
						<Col sm={12} md={3}>
							<FormLabel>Group</FormLabel>
							<FormSelect
								name="group"
								onChange={(e) => setGroupFilter(e ? (e as SelectOption<string>).value : '')}
								options={groupOptions || []}
								value={groupFilter}
								isClearable
								isSearchable
							/>
						</Col>
						<Col sm={12} md={3}>
							<FormLabel>Client</FormLabel>
							<FormSelect
								name="company"
								onChange={(e) => setClientFilter(e ? (e as SelectOption<string>).value : '')}
								options={clientOptions || []}
								value={clientFilter}
								isClearable
								isSearchable
							/>
						</Col>
						<Col sm={12} md={3}>
							<FormLabel>Partner</FormLabel>
							<FormSelect
								name="partner"
								onChange={(e) => setDirectorFilter(e ? (e as SelectOption<string>).value : '')}
								options={directorOptions || []}
								value={directorFilter}
								isClearable
								isSearchable
							/>
						</Col>
					</Row>
					<Row className="py-3">
						<Col sm={5} md={2}>
							<FormDate
								name="costStartDate"
								label="Cost Start Date"
								value={costStartDateFilter?.toISODate()}
								onChange={(e) => setCostStartDateFilter(e.target.value === '' ? null : DateTime.fromISO(e.target.value))}
							/>
						</Col>
						<Col sm={5} md={2}>
							<FormDate
								name="costEndDate"
								label="Cost End Date"
								value={costEndDateFilter?.toISODate()}
								onChange={(e) => setCostEndDateFilter(e.target.value === '' ? null : DateTime.fromISO(e.target.value))}
							/>
						</Col>
					</Row>
					<Row className="py-3">
						<Col sm={5} md={2}>
							<FormDate
								name="uploadStartDate"
								label="Upload Start Date"
								value={uploadStartDateFilter?.toISODate()}
								onChange={(e) => setUploadStartDateFilter(e.target.value === '' ? null : DateTime.fromISO(e.target.value))}
							/>
						</Col>
						<Col sm={5} md={2}>
							<FormDate
								name="uploadEndDate"
								label="Upload End Date"
								value={uploadEndDateFilter?.toISODate()}
								onChange={(e) => setUploadEndDateFilter(e.target.value === '' ? null : DateTime.fromISO(e.target.value))}
							/>
						</Col>
						<Col style={{ display: 'flex', alignItems: 'flex-end' }}>
							<div
								style={{
									width: '100%',
									height: '50px',
									alignItems: 'center',
									flexDirection: 'row-reverse',
									display: 'flex',
									color: 'red',
								}}
							></div>
						</Col>
						<Col sm={2} md={2} style={{ alignSelf: 'flex-end' }}>
							<Button onClick={() => handleNewFilterSearch()} style={{ width: '100%' }} disabled={!filterHasChangedWithoutSearch}>
								Search
							</Button>
						</Col>
					</Row>
				</>
			)}
			<Row>
				<Col>
					<Listing<CostWithJobInformation>
						list={costs || []}
						columns={[
							{
								value: (item) => item.job.clientName,
								render: (item) => <div>{item.job.clientName}</div>,
								showHeader: true,
								headerText: 'Client Name',
								sortColumnName: 'clientName',
							},
							{
								value: (item) => item.job.jobName,
								render: (item) => <div>{item.job.jobName}</div>,
								showHeader: true,
								headerText: 'Job',
								sortColumnName: 'jobName',
							},
							{
								value: (item) => item.date,
								render: (item) => <div className="text-end">{DateTime.fromISO(item.date).toFormat('dd/MM/yyyy')}</div>,
								showHeader: true,
								headerText: 'Cost Date',
								sortColumnName: 'costDate',
							},
							{
								value: (item) => item.createTs,
								render: (item) => <div className="text-end">{DateTime.fromISO(item.createTs).toFormat('dd/MM/yyyy')}</div>,
								showHeader: true,
								headerText: 'Upload Date',
								sortColumnName: 'uploadDate',
							},
							{
								value: (item) => item.createUserName,
								render: (item) => <div>{item.createUserName}</div>,
								showHeader: true,
								headerText: 'Upload User',
								sortColumnName: 'uploadUser',
							},
							{
								value: (item) => item.job.jobNumber,
								render: (item) => <div>{item.job.jobNumber}</div>,
								showHeader: true,
								headerText: 'Job Number',
								sortColumnName: 'jobNumber',
							},
							{
								value: (item) => item.description,
								render: (item) => <div>{item.description}</div>,
								showHeader: true,
								headerText: 'Description',
								sortColumnName: 'description',
							},
							{
								value: (item) => String(item.quantity),
								render: (item) => <div className="text-end">{item.quantity}</div>,
								showHeader: true,
								headerText: 'Quantity',
								headerClass: 'text-end',
								sortColumnName: 'quantity',
							},
							{
								value: (item) => String(item.cost),
								render: (item) => <div className="text-end">{dollarFormat(item.cost, true)}</div>,
								showHeader: true,
								headerText: 'Unit Cost',
								headerClass: 'text-end',
								sortColumnName: 'unitCost',
							},
							{
								value: (item) => String(item.cost * item.quantity),
								render: (item) => <div className="text-end">{dollarFormat(item.cost * item.quantity, true)}</div>,
								showHeader: true,
								headerText: 'Cost',
								headerClass: 'text-end',
								sortColumnName: 'cost',
							},
							{
								value: (item) => String(item.price),
								render: (item) => <div className="text-end">{dollarFormat(item.price, true)}</div>,
								showHeader: true,
								headerText: 'Price',
								headerClass: 'text-end',
								sortColumnName: 'price',
							},
							{
								value: (item) => String(item.price * item.quantity),
								render: (item) => <div className="text-end">{dollarFormat(item.value, true)}</div>,
								showHeader: true,
								headerText: 'Total',
								sortColumnName: 'total',
							},
						]}
						customButtons={[
							<Dropdown as={ButtonGroup} key="upload">
								<Button onClick={() => setShowModal(true)} disabled={!I.have('admin', appState.permission.roleID)}>
									Upload
								</Button>
								<Dropdown.Toggle split />
								<Dropdown.Menu>
									<Dropdown.Item onClick={handleDownloadTemplate}>Download CSV Template</Dropdown.Item>
								</Dropdown.Menu>
							</Dropdown>,
						]}
						name="Cost"
						namePlural="Costs"
						getIDFunc={(item) => item.costID}
						defaultSort={{ column: 'id', order: 'ASC' }}
						isLoading={costs === null}
						paginationControls="both"
						borderRadius
						boxShadow
						smallPagination
						headerPadRow
						hideDisplayCount
					/>
				</Col>
			</Row>

			{showModal && <UploadModal onClose={() => setShowModal(false)} handleBulkCostInsert={handleCostInsert} />}
			{showLoadingModal && <LoadingModal percentageComplete={loadingModalPercentage} message={loadingModalMessage} onClose={handleLoadingClose} />}
		</Container>
	)
}

export default Costs
